import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity,
  Server,
  Database,
  Cpu,
  HardDrive,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Zap,
  Users,
  FileText,
  MessageSquare,
  Clock,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SystemMonitoring = () => {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, metricsRes] = await Promise.all([
        axios.get(`${API}/api/v2/system/health`),
        axios.get(`${API}/api/v2/system/metrics`)
      ]);
      setHealth(healthRes.data);
      setMetrics(metricsRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch system data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-emerald-600 bg-emerald-100';
      case 'degraded': return 'text-amber-600 bg-amber-100';
      case 'unhealthy': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
      case 'degraded': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'unhealthy': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Activity className="w-5 h-5 text-slate-600" />;
    }
  };

  if (loading && !health) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="system-monitoring">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-600" />
              System Monitoring (NOC)
            </h1>
            <p className="text-slate-500 mt-1">Real-time system health and performance metrics</p>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-sm text-slate-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Overall Status */}
        <Card className={`border-0 shadow-lg ${getStatusColor(health?.status)}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getStatusIcon(health?.status)}
                <div>
                  <h2 className="text-xl font-bold">System Status: {health?.status?.toUpperCase()}</h2>
                  <p className="text-sm opacity-80">All core services are operational</p>
                </div>
              </div>
              <Badge className={`${getStatusColor(health?.status)} text-sm px-4 py-2`}>
                {health?.status === 'healthy' ? 'All Systems Go' : 'Attention Required'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Metrics */}
        <Tabs defaultValue="database" className="space-y-4">
          <TabsList className="bg-white border">
            <TabsTrigger value="database">Database Metrics</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="processing">Processing</TabsTrigger>
          </TabsList>

          <TabsContent value="database">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-slate-600" />
                  Database Collections
                </CardTitle>
                <CardDescription>Document counts across all collections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {metrics && Object.entries(metrics).filter(([key]) =>
                    !['today_rfps_created', 'today_comments', 'rfps_planning', 'rfps_active', 'rfps_on_hold', 'rfps_submitted', 'rfps_closed'].includes(key)
                  ).map(([name, data]) => (
                    <div key={name} className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-800">{data?.count || 0}</p>
                      <p className="text-xs text-slate-500 capitalize">{name.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-slate-600" />
                  Today's Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-xl p-6 text-center">
                    <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-blue-700">{metrics?.today_rfps_created || 0}</p>
                    <p className="text-sm text-blue-600">RFPs Created Today</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-6 text-center">
                    <MessageSquare className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-green-700">{metrics?.today_comments || 0}</p>
                    <p className="text-sm text-green-600">Comments Today</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-6 text-center">
                    <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-purple-700">{health?.metrics?.total_users || 0}</p>
                    <p className="text-sm text-purple-600">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="processing">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Project Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-slate-700">{metrics?.rfps_planning || 0}</p>
                    <p className="text-sm text-slate-500">Planning</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-blue-700">{metrics?.rfps_active || 0}</p>
                    <p className="text-sm text-blue-600">Active</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-amber-700">{metrics?.rfps_on_hold || 0}</p>
                    <p className="text-sm text-amber-600">On Hold</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-emerald-700">{metrics?.rfps_submitted || 0}</p>
                    <p className="text-sm text-emerald-600">Submitted</p>
                  </div>
                  <div className="bg-slate-100 rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-slate-600">{metrics?.rfps_closed || 0}</p>
                    <p className="text-sm text-slate-500">Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SystemMonitoring;
